/**
 * Dies habituals d'entrega d'un proveïdor — funcions PURES (Compres).
 *
 * QUÈ RESOL. Un proveïdor no reparteix cada dia: passa els dimarts i els divendres,
 * o només els dilluns. Qui fa la comanda ho sap de memòria i ho escriu a mà cada
 * vegada, i quan s'equivoca la data d'entrega prevista deixa de servir per a res
 * —ni per saber què arriba demà, ni per veure què va tard. Si el mestre ho sap,
 * la pantalla ho pot proposar (CLAUDE.md: «tot el que es pugui derivar, es deriva»).
 *
 * NUMERACIÓ ISO-8601: 1 = dilluns … 7 = diumenge. És la de Postgres
 * (`EXTRACT(ISODOW FROM date)`), i per això la columna desa exactament això. NO és
 * la de JavaScript, que compta 0 = diumenge; la conversió es fa aquí dins i enlloc
 * més, que és on es pot provar.
 *
 * PURITY: aritmètica de dates ISO, sense rellotge implícit — la data de partida
 * entra per paràmetre.
 */

/** Dia de la setmana en numeració ISO-8601: 1 = dilluns … 7 = diumenge. */
export const ISO_WEEKDAYS = [1, 2, 3, 4, 5, 6, 7] as const;
export type IsoWeekday = (typeof ISO_WEEKDAYS)[number];

/** Cert si el número és un dia de la setmana ISO vàlid. */
export function isIsoWeekday(value: unknown): value is IsoWeekday {
  return typeof value === 'number' && Number.isInteger(value) && value >= 1 && value <= 7;
}

/**
 * Els dies vàlids d'una configuració, ordenats i sense repeticions. El que arriba
 * de la base de dades o d'un formulari pot portar brossa; aquí es queda.
 */
export function normalizeWeekdays(values: readonly unknown[] | null | undefined): IsoWeekday[] {
  if (!values) return [];
  const seen = new Set<IsoWeekday>();
  for (const value of values) if (isIsoWeekday(value)) seen.add(value);
  return [...seen].sort((a, b) => a - b);
}

/** Dia ISO d'una data `YYYY-MM-DD`, o null si la data no es pot llegir. */
function isoWeekdayOf(isoDate: string): IsoWeekday | null {
  const ms = Date.parse(`${isoDate}T00:00:00Z`);
  if (Number.isNaN(ms)) return null;
  // getUTCDay(): 0 = diumenge … 6 = dissabte. ISO vol el diumenge al final.
  const js = new Date(ms).getUTCDay();
  return (js === 0 ? 7 : js) as IsoWeekday;
}

/** La data `YYYY-MM-DD` que hi ha `days` dies després de `isoDate`. */
function addDays(isoDate: string, days: number): string {
  const ms = Date.parse(`${isoDate}T00:00:00Z`) + days * 86_400_000;
  return new Date(ms).toISOString().slice(0, 10);
}

/**
 * La propera entrega d'un proveïdor a partir d'una data.
 *
 * ESTRICTAMENT DESPRÉS de `fromIsoDate`. Una comanda que es fa avui no arriba
 * avui, per molt que avui toqui repartiment: quan es passa la comanda el camió ja
 * ha sortit. Proposar el mateix dia seria proposar una data que ja neix tard.
 *
 * @param fromIsoDate       Data de la comanda (`YYYY-MM-DD`). Explícit: cap rellotge amagat.
 * @param deliveryWeekdays  Dies habituals del proveïdor, en numeració ISO.
 * @returns La data de la propera entrega, o `null` quan el proveïdor no en té cap
 *          de configurat (llavors no hi ha res a proposar i el camp es deixa a
 *          l'usuari) o quan la data de partida no es pot llegir.
 */
export function nextDeliveryDate(
  fromIsoDate: string,
  deliveryWeekdays: readonly unknown[] | null | undefined,
): string | null {
  const days = normalizeWeekdays(deliveryWeekdays);
  if (days.length === 0) return null;
  if (isoWeekdayOf(fromIsoDate) === null) return null;
  // Com a molt set passes: amb un dia configurat, el pitjor cas és una setmana.
  for (let offset = 1; offset <= 7; offset += 1) {
    const candidate = addDays(fromIsoDate, offset);
    const weekday = isoWeekdayOf(candidate);
    if (weekday !== null && days.includes(weekday)) return candidate;
  }
  return null;
}

/**
 * Cert quan la data cau en un dia de repartiment del proveïdor.
 *
 * Una entrega fora del dia habitual és una EXCEPCIÓ, no un error (OBR-69): la
 * pantalla ho avisa i deixa desar, perquè de vegades el proveïdor passa quan no
 * toca i la comanda ho ha de poder dir.
 *
 * @returns `null` quan no hi ha cap regla per complir ni per trencar: el proveïdor
 *          no té dies configurats, o la data no es pot llegir.
 */
export function isUsualDeliveryDay(
  isoDate: string,
  deliveryWeekdays: readonly unknown[] | null | undefined,
): boolean | null {
  const days = normalizeWeekdays(deliveryWeekdays);
  if (days.length === 0) return null;
  const weekday = isoWeekdayOf(isoDate);
  if (weekday === null) return null;
  return days.includes(weekday);
}

/**
 * Cert quan l'entrega prevista cau ABANS de la data de comanda.
 *
 * Això sí que és una inconsistència, i no s'ha de poder desar (OBR-69): res no
 * arriba abans d'haver-ho demanat. El mateix dia sí que val —una entrega urgent—,
 * i sense entrega prevista, o amb una data que no es pot llegir, no hi ha res a dir.
 */
export function deliveryBeforeOrder(
  orderIsoDate: string,
  deliveryIsoDate: string | null | undefined,
): boolean {
  if (!deliveryIsoDate) return false;
  const order = Date.parse(`${orderIsoDate}T00:00:00Z`);
  const delivery = Date.parse(`${deliveryIsoDate}T00:00:00Z`);
  if (Number.isNaN(order) || Number.isNaN(delivery)) return false;
  return delivery < order;
}
